import {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import {SoopButtonCommon, SoopLoading} from '@soop-ui/soop-ui';
import Title from '@/components/help/common/Title';
import {ROUTE} from '@/constants/main/route';
import {HELP_LOG} from '@/constants/help';
import {
  useGetMyInquiryDetail,
  useGetIsEvaluatedInquiry,
  myInquiryInitialData,
  useAutoCloseDialog,
} from '@/hooks/help';
import useBackgroundLocation from '@/hooks/useBackgroundLocation';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import {sendHelpClickLog} from '@/utils/help/log';
import SurveyLayer from '../SurveyLayer';
import styles from './MyInquiryDetail.module.scss';

const HelpMyInquiryDetail = () => {
  const {isMobile} = useHelpContext();
  const {inquiryId = ''} = useParams();
  const {data, isLoading} = useGetMyInquiryDetail({inquiryId});
  const {category, question, answer, isError, isObjection} =
    data || myInquiryInitialData;
  const {
    data: isEvaluated,
    refetch,
    isLoading: isEvaluatedLoading,
  } = useGetIsEvaluatedInquiry({
    hasAnswer: !!answer.createdDate && !isObjection,
    inquiryId,
  });
  const navigate = useNavigate();
  const {navigateWithBackgroundLocation} = useBackgroundLocation();
  const {openDialog} = useAutoCloseDialog();

  const navigateToMyInquiry = () => {
    navigate(ROUTE.HELP.MY_INQUIRY);
  };

  const formatDate = (date?: string) => {
    return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '';
  };

  const handleOpenSurveyLayer = () => {
    if (isMobile) {
      navigateWithBackgroundLocation(ROUTE.HELP.LAYER_MY_INQUIRY_SURVEY, {
        state: {inquiryId},
        preventScrollReset: true,
      });
    } else {
      openDialog({
        component: () => (
          <SurveyLayer
            isMobile={false}
            inquiryId={inquiryId}
            onSuccess={refetch}
          />
        ),
        scrollLock: true,
      });
    }

    sendHelpClickLog(HELP_LOG.CODE_TYPE.INQUIRY, {
      buttonType: HELP_LOG.BUTTON_TYPE.MY_FEEDBACK,
    });
  };

  useEffect(() => {
    if (isError) {
      navigate(ROUTE.HELP.MY_INQUIRY, {replace: true});
    }
  }, [isError, navigate]);

  return (
    <>
      {!isMobile && <Title title='나의 문의/신고내역' align='left' />}
      <div className={styles.DetailsWrapper}>
        {isLoading ? (
          <SoopLoading isDevice={isMobile ? 'mobile' : 'desktop'} />
        ) : (
          <div className={styles.DetailsContent}>
            <div className={styles.DetailsInner}>
              <div className={styles.type}>{category} </div>
              <div className={styles.DetailsTitleArea}>
                <h2>{question.title}</h2>
                <span>{formatDate(question.createdDate)}</span>
              </div>
              <div
                className={styles.QuestionContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(question.contents),
                }}
              />
              {answer.createdDate && (
                <div className={styles.AnswerArea}>
                  <div className={styles.AnswerTitleArea}>
                    <div className={styles.NameArea}>
                      <div className={styles.thumb}>
                        <img
                          src='//res.sooplive.co.kr/images/svg/cs_thumb_profile.svg'
                          alt=''
                        />
                      </div>
                      <div className={styles.DataArea}>
                        <span className={styles.nick}>숲지기</span>
                        <span className={styles.CreatedDate}>
                          {formatDate(answer.createdDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className={styles.AnswerTitle}>
                    안녕하세요. SOOP 고객센터입니다.
                  </p>
                  <div
                    className={styles.AnswerContent}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(answer.contents, {
                        ADD_ATTR: ['target'],
                      }),
                    }}
                  />
                  {!isObjection && (
                    <SoopButtonCommon
                      variant='defaultPrimary'
                      disabled={isEvaluatedLoading || isEvaluated}
                      size={36}
                      onClick={handleOpenSurveyLayer}>
                      답변 만족도 평가
                    </SoopButtonCommon>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div className={styles.BtnArea}>
          <SoopButtonCommon
            className={styles.BtnBack}
            variant={isMobile ? 'lineSecondary' : 'defaultTertiary'}
            size={52}
            onClick={navigateToMyInquiry}>
            목록
          </SoopButtonCommon>
        </div>
      </div>
    </>
  );
};

export default HelpMyInquiryDetail;
